import {
  ApolloError,
  NetworkStatus,
  OperationVariables,
  QueryDataOptions,
  QueryHookOptions,
  QueryResult,
  useQuery,
} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {ReactNode, useEffect} from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import * as Logging from 'util/Logging';

import {getQueryName} from './AST';
import notifyGenericGraphQLError from './notifyGenericGraphQLError';
import {OpSyncState} from './sync/OpSyncState';

export enum QueryResultType {
  PENDING = 'PENDING',
  SKIPPED = 'SKIPPED',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

interface BenchlingQueryOptions {
  renderCachedDataWhenRefetching?: boolean;
}

interface BenchlingQueryHookOptions<TData, TVariables extends OperationVariables = OperationVariables>
  extends QueryHookOptions<TData, TVariables>,
    BenchlingQueryOptions {}

interface BenchlingQueryComponentOptions<TData, TVariables extends OperationVariables = OperationVariables>
  extends Omit<QueryDataOptions<TData, TVariables>, 'children'>,
    BenchlingQueryOptions {
  children: (result: QueryComponentReturnType<TData>) => JSX.Element;
}

interface PendingReturnType<TData> extends QueryResult<TData> {
  type: QueryResultType.PENDING;
  data: undefined;
  loading: true;
}

interface ErrorReturnType extends Omit<QueryResult, 'data' | 'loading'> {
  type: QueryResultType.ERROR;
}

export interface SuccessReturnType<TData> extends QueryResult<TData> {
  type: QueryResultType.SUCCESS;
  data: TData;
}

type QueryReturnType<TData> = PendingReturnType<TData> | ErrorReturnType | SuccessReturnType<TData>;

type QueryReturnFields = 'data' | 'error' | 'loading';

interface SkippedReturnType extends QueryResult {
  type: QueryResultType.SKIPPED;
  data: undefined;
}

type SkippableQueryReturnType<TData> = QueryReturnType<TData> | SkippedReturnType;

type QueryComponentReturnType<TData> =
  | PendingReturnType<TData>
  | SuccessReturnType<TData>
  | SkippedReturnType;

type WithoutReturnType<TData, TVariables extends OperationVariables = OperationVariables> = Omit<
  QueryResult<TData, TVariables>,
  QueryReturnFields
>;

export type BenchlingQueryResult<
  TData,
  TVariables extends OperationVariables = OperationVariables,
> = QueryReturnType<TData> & WithoutReturnType<TData, TVariables>;

export type SkippableBenchlingQueryResult<
  TData,
  TVariables extends OperationVariables = OperationVariables,
> = SkippableQueryReturnType<TData> & WithoutReturnType<TData, TVariables>;

// Query component support
type BenchlingQueryProps<TData, TVariables extends OperationVariables = OperationVariables> = Omit<
  BenchlingQueryComponentOptions<TData, TVariables>,
  'children'
> & {
  variables?: TVariables;
  renderLoading: ({refetch}: {refetch: QueryResult['refetch']}) => ReactNode;
  children: (data: TData, result: Omit<QueryComponentReturnType<TData>, 'data'>) => ReactNode;
};

function isMissingMockError(error: Error): boolean {
  return (
    Boolean(global.__NODE_TESTING__) && error.message.startsWith('No more mocked responses for the query')
  );
}

// Helper function for determining whether to render special cached data result state
function shouldRenderCachedDataWhenRefetching<TData>(
  networkStatus: NetworkStatus,
  syncState: OpSyncState,
  renderCachedData: boolean,
  dataCache: TData | undefined
): boolean {
  // refetch status is used when the caller invokes the 'refetch' function manually. The setVariables status
  // is used when the network policy is 'cache-and-network' and the caller changes the query variables,
  // triggering a refetch by Apollo.
  return Boolean(
    (networkStatus === NetworkStatus.refetch ||
      networkStatus === NetworkStatus.setVariables ||
      syncState === OpSyncState.WILL_SYNC) &&
      renderCachedData &&
      dataCache &&
      !_.isEmpty(dataCache)
  );
}

function didError(error: ApolloError | undefined, networkStatus: NetworkStatus): boolean {
  // react-apollo v2.5.7 introduced a bug such that if a component fires a query that errors, and something
  // causes that component to re-render, it'll re-render with a null error and empty data object. This causes
  // errors in the component rendering code because it assumes data is hydrated.
  // The next version after 2.5.7 is a major version bump which will likely take some time to upgrade to.
  // We'll revisit this when we upgrade.
  // https://github.com/apollographql/react-apollo/blob/master/Changelog.md#257-2019-06-21
  return Boolean(error) || networkStatus === NetworkStatus.error;
}

function handleApolloError<TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options?: BenchlingQueryHookOptions<TData, TVariables>,
  error?: ApolloError
): void {
  // See comment in didError - error may be undefined even if an error has occurred. Assume that if so, we've
  // already handled the error once and don't need to do it again.
  if (!error) {
    return;
  }

  // We don't expect resolvers to throw errors (authz errors are expected to be returned as part of
  // data in a union type, e.g. Foo | InaccessibleInfo).
  // While this is somewhat redundant with our error handling in createErrorLink.tsx, this helps when
  // queries fail in tests or Storybook.
  if (isMissingMockError(error)) {
    // For now, don't error for missing mocks - this will likely happen due to parent components
    // rendering child components that perform GraphQL query (presumably not testing anything in the
    // child); this roughly matches the behavior of our previous REST code - network calls are stubbed
    // to fail (which just fires a notification), so the React components either render null or a
    // throbber.

    const withVariablesText =
      options?.variables != null ? ` with variables: ${JSON.stringify(options.variables)}` : '';

    // eslint-disable-next-line no-console
    console.log(`[GraphQL error] missing mock for query ${getQueryName(query)}${withVariablesText}`);
  } else {
    // eslint-disable-next-line no-console
    console.error('[GraphQL error] ', error);
  }
}

function logUnknownQueryError<TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  data: TData,
  loading: boolean,
  error: ApolloError | undefined,
  result: QueryResult<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>
): void {
  // We continue to see errors from components accessing undefined data returned by Apollo query. This
  // will hopefully give us more insight into additional clauses we should guard against.
  Logging.error(`${getQueryName(query)}: Unexpected value for data given not loading and no error`, {
    extra: {data, loading, error, networkStatus: result.networkStatus, ...options},
  });

  notifyGenericGraphQLError();
}

// Helper function for abstracting some of the grittier bits of useSkippableBenchlingQuery
function useCachedRefetchQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options?: BenchlingQueryHookOptions<TData, TVariables>
): {
  result: QueryResult<TData, TVariables>;
  renderCachedDataWhenRefetching: boolean;
  syncState: OpSyncState;
  dataCache: TData | undefined;
} {
  const [syncState, setSyncState] = useStateIfMounted(OpSyncState.NOT_OBSERVED);
  const [dataCache, setDataCache] = useStateIfMounted<TData | undefined>(undefined);

  const {renderCachedDataWhenRefetching, ...queryOptions} = options ?? {};

  if (renderCachedDataWhenRefetching) {
    queryOptions.notifyOnNetworkStatusChange = true;
  }

  // NB: Do NOT pass unstable functions to context - it triggers re-renders at times that Apollo doesn't seem
  // to expect, which can lead to weird errors. See #12441 for more information.
  const result = useQuery(query, {...queryOptions, context: {setSyncState}});
  const {data} = result;

  useEffect(() => {
    if (renderCachedDataWhenRefetching && !_.isEmpty(data)) {
      setDataCache(data);
    }
  }, [setDataCache, data, renderCachedDataWhenRefetching]);

  return {
    result,
    renderCachedDataWhenRefetching: Boolean(renderCachedDataWhenRefetching),
    syncState,
    dataCache,
  };
}

/**
 * Hook that wraps Apollo's useQuery with a more opinionated interface for better type-safety and error
 * handling. Similar to useBenchlingQuery, but additionally handles the skip option and subsequent result
 * types appropriately.
 */
export function useSkippableBenchlingQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options?: BenchlingQueryHookOptions<TData, TVariables>
): SkippableBenchlingQueryResult<TData, TVariables> {
  const {
    result,
    renderCachedDataWhenRefetching: renderCachedData,
    syncState,
    dataCache,
  } = useCachedRefetchQuery(query, options);
  const {loading, error, data, networkStatus, ...otherResultFields} = result;

  if (options?.skip) {
    assertAndContinue(!data, 'useBenchlingQuery: Expected Apollo to return data=undefined when skip=true');
    const skipResultFields = {loading, error, networkStatus};
    return {type: QueryResultType.SKIPPED, ...otherResultFields, ...skipResultFields, data: undefined};
  } else if (didError(error, networkStatus)) {
    handleApolloError(query, options, error);
    return {type: QueryResultType.ERROR, ...otherResultFields, error, networkStatus};
  } else if (shouldRenderCachedDataWhenRefetching(networkStatus, syncState, renderCachedData, dataCache)) {
    // Some UXs prefer to show stale data while refetching in the background. In this case, serve cached data
    // until syncState === OpSyncState.SYNC_COMPLETE. We continue to pass down networkStatus unaltered, so
    // components can render a loading component AND cached data by conditioning on NetworkStatus.refetch as
    // well as the loading field if desired.
    const cachedDataResultFields = {data: dataCache as TData, loading, networkStatus};
    return {type: QueryResultType.SUCCESS, ...otherResultFields, ...cachedDataResultFields};
  } else if (loading || syncState === OpSyncState.WILL_SYNC) {
    // Even if loading is false, don't render when the syncState is WILL_SYNC. See JSDoc on OpSyncState for
    // more info.
    //
    // syncState === OpSyncState.NOT_OBSERVED is safe to render data for - if the data is being served, it
    // must have come from cache (otherwise it'd have been marked as WILL_SYNC by the sync system).
    const loadingResultFields = {networkStatus, data: undefined};
    return {type: QueryResultType.PENDING, loading: true, ...otherResultFields, ...loadingResultFields};
  } else if (data && !_.isEmpty(data)) {
    // isEmpty is functionally sufficient to guard against falsey values for data, but we add the extra check
    // here as a type assertion to inform TS that data is not undefined.
    const successResultFields = {data, loading, networkStatus};
    return {type: QueryResultType.SUCCESS, ...otherResultFields, ...successResultFields};
  } else {
    logUnknownQueryError(query, data, loading, error, result, options);

    // Components using useSkippableBenchlingQuery almost certainly aren't expecting null/undefined/empty
    // object. Rather than trigger an error downstream, just treat as error.
    const unknownErrorResultFields = {error, networkStatus};
    return {type: QueryResultType.ERROR, ...otherResultFields, ...unknownErrorResultFields};
  }
}

/**
 * Hook that wraps Apollo's useQuery with a more opinionated interface for better type-safety and error
 * handling. If you don't need custom rendering behavior for error and loading states, then prefer using
 * <BenchlingQuery>.
 *
 * One example of this is splitting queries for better cache behavior:
 * https://www.apollographql.com/docs/angular/recipes/query-splitting/
 *
 * To skip queries, use useSkippableBenchlingQuery and pass in the skip option.
 *
 * We proxy most props through to Apollo's useQuery. See more detail here:
 * https://www.apollographql.com/docs/react/data/queries/#usequery-api
 *
 * Usage:
 * @example
 *    // Without variables
 *    const result = useBenchlingQuery<GetFoo>(FOO_QUERY);
 *    if (result.type === QueryResultType.ERROR) {
 *      // useBenchlingQuery reports errors, but we are still responsible for rendering
 *      return null;
 *    } else if (result.type === QueryResultType.PENDING) {
 *      return <HorizontalThrobber />;
 *    } else {
 *      return <div>{result.data.foo.bar}</div>
 *    }
 *
 *    // With variables
 *    const result = useBenchlingQuery<GetFoo, GetFooVariables>(FOO_QUERY, { variables: { fooId }});
 *    if (result.type === QueryResultType.ERROR) {
 *      return null;
 *    } else if (result.type === QueryResultType.PENDING) {
 *      return <HorizontalThrobber />;
 *    } else {
 *      return <div>{result.data.foo.bar}</div>
 *    }
 */
export function useBenchlingQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options?: Omit<BenchlingQueryHookOptions<TData, TVariables>, 'skip'> & {skip?: never}
): BenchlingQueryResult<TData, TVariables> {
  const result = useSkippableBenchlingQuery(query, options);
  assertAndContinue(result.type !== QueryResultType.SKIPPED, 'Used the wrong query hook!');
  return result as QueryReturnType<TData> & WithoutReturnType<TData, TVariables>;
}

/**
 * This is a thinner wrapper around Apollo's <Query> component that only handles errors. Generally prefer
 * BenchlingQuery, but this is necessary in query-splitting circumstances, in which a component needs to fire
 * multiple queries for performance reasons (they can be parallelized, and have better cache leverage).
 */
export function ErrorHandlingQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  props: BenchlingQueryComponentOptions<TData, TVariables>
): JSX.Element | null {
  const {children, query, ...otherProps} = props;
  const result = useSkippableBenchlingQuery(query, otherProps);

  // useBenchlingQuery handles the error, but we still need to return a ReactNode
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else {
    return children(result);
  }
}

/**
 * Component that wraps Apollo's <Query> component with a more opinionated interface.
 *
 * Usage:
 *
 *    // Without variables
 *    <BenchlingQuery<GetFoo> query={GET_FOO} renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}>
 *      {(data) => <div>{data.foo.bar}</div>}
 *    </BenchlingQuery>
 *
 *    // With variables
 *    <BenchlingQuery<GetFoo, GetFooVariables>
 *      query={GET_FOO}
 *      variables={props.fooId}
 *      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
 *    >
 *      {(data) => <div>{data.foo.bar}</div>}
 *    </BenchlingQuery>
 *
 * Note that DEFAULT_RENDER_LOADING is not a default prop to encourage devs to think about what loading state
 * makes the most sense.
 *
 * Main differences:
 *   - To smooth the migration process, we simply render `null` when resolvers throw exceptions. We don't
 *     expect them to throw for authz (though this may occur when migrating legacy backend code), so generally
 *     it'd only be for hard errors. We may add support for better presentation when resolvers throw non-authz
 *     exceptions, but this roughly matches previous behavior.
 *   - Factor the (data, loading, error) => component child function into a renderLoading prop and a children
 *     function that only takes a non-null data. This avoids otherwise-necessary type assertions on all data
 *     accesses.
 *
 * We proxy most props through to Apollo's Query. See more detail here:
 * https://www.apollographql.com/docs/react/api/react-components/#query
 */
export default function BenchlingQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  props: BenchlingQueryProps<TData, TVariables>
): JSX.Element {
  const {children: renderData, renderLoading, ...otherProps} = props;
  return (
    <ErrorHandlingQuery<TData, TVariables> {...otherProps}>
      {({data, ...result}: QueryComponentReturnType<TData>) => {
        if (result.type === QueryResultType.PENDING) {
          return <>{renderLoading({refetch: result.refetch})}</>;
        } else {
          return <>{renderData(data!, result)}</>;
        }
      }}
    </ErrorHandlingQuery>
  );
}

BenchlingQuery.DEFAULT_RENDER_LOADING = () => <HorizontalThrobber />;
